import { Box, CircularProgress, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import Context from '../Context'
import { useClock } from '../utils/clock'
import theme from '../theme'
import useInterval from '../utils/useInterval'
import { apiJson, apiFetch } from '../utils/api'
import { useNavigate } from 'react-router-dom'
import { pathForArb } from '../utils/routes'
import { playAlert } from '../utils/sound'

const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

const FIRST_PAGE = 7
const MAX_PAGE = 50

// fifty arbs arriving together is one event, not fifty; anything closer than this rides on the first
const ALERT_GAP_MS = 20000

const supported = () => typeof window !== 'undefined' && 'Notification' in window

const timeAgo = (iso) => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (seconds < 60) {
    return `${seconds}s ago`
  }
  if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m ago`
  }
  return `${Math.round(seconds / 3600)}h ago`
}

function MuteButton({ children, onClick }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        border: '1px solid #D6D6DC',
        backgroundColor: 'white',
        borderRadius: '2px',
        padding: '4px 10px',
        fontFamily: FONT,
        fontSize: '12px',
        color: '#6E7378',
        cursor: 'pointer',
        '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main }
      }}
    >
      {children}
    </Box>
  )
}

function NotificationsPage() {
  const { marketHost, settings, setLastResponseTime, setRequestCount } = useContext(Context)
  const clock = useClock()
  const navigate = useNavigate()

  const [ items, setItems ] = useState(null)
  const [ expanded, setExpanded ] = useState(false)
  const [ error, setError ] = useState('')

  const known = useRef(new Set())
  const lastAlert = useRef(0)
  const pending = useRef([])
  const holdTimer = useRef(null)
  const primed = useRef(false)

  /**
   * The poll callback outlives the render that created it, so reading a setting straight out of it can
   * answer with whatever was true when the timer was set up. The ref is always this render's copy.
   */
  const live = useRef(settings)
  live.current = settings

  const load = useCallback(async () => {
    try {
      const data = await apiJson(marketHost, `/api/notifications?limit=${MAX_PAGE}`)
      const list = data.notifications || []
      setItems(list)
      setError('')
      // this screen refreshes itself, so the clock in the bar tracks this rather than freezing
      setRequestCount((prev) => prev + 1)
      setLastResponseTime(Date.now())

      /**
       * The first load is the backlog, not news: alerting on it would greet everyone who opens the page
       * with a wall of sound for opportunities that have been sitting there. Only what appears after
       * that counts as arrived.
       */
      const fresh = list.filter((item) => !known.current.has(item.id))
      list.forEach((item) => known.current.add(item.id))

      if (!primed.current) {
        primed.current = true
        return
      }
      if (fresh.length) {
        announce(fresh)
      }
    } catch (e) {
      setError(e.message || 'Could not load notifications')
    }
    // eslint-disable-next-line
  }, [marketHost])

  /**
   * Arbs arrive in bursts, and one banner per arb is a siren. The quiet window is there for that — but
   * it used to drop whatever landed inside it, which is why a second arb seconds after the first made
   * no sound and no banner and read as the thing being broken. Now the window holds them and sends one
   * notification for the lot when it is up: nothing is lost and nothing rattles.
   */
  const announce = (fresh) => {
    const now = Date.now()
    const waited = now - lastAlert.current

    if (waited < ALERT_GAP_MS) {
      pending.current = pending.current.concat(fresh)
      if (!holdTimer.current) {
        holdTimer.current = setTimeout(() => {
          holdTimer.current = null
          const held = pending.current
          pending.current = []
          if (held.length) {
            announce(held)
          }
        }, ALERT_GAP_MS - waited)
      }
      return
    }

    lastAlert.current = now

    playAlert(0.5)

    // two separate gates: the browser decides whether it may, the account decides whether it should
    if (live.current.desktopNotifications !== false && supported() && Notification.permission === 'granted') {
      const first = fresh[0]
      const title = fresh.length === 1
        ? `Arb ${first.profit.toFixed(2)}% · ${first.home} vs ${first.away}`
        : `${fresh.length} new arbs`
      const body = fresh.length === 1
        ? `${first.market}\n${first.legs.map((leg) => `${leg.bookmaker} ${leg.price}`).join('   ')}`
        : fresh.slice(0, 3).map((item) => `${item.profit.toFixed(2)}%  ${item.home} vs ${item.away}`).join('\n')
      // a tag per batch: a replacement draws no banner, and every batch is its own piece of news
      const notification = new Notification(title, { body, icon: '/logo192.png', tag: `arb-${now}` })

      /**
       * Clicking it should land on the thing it is about. A single arb goes straight to that arb; a
       * batch goes to the feed, because there is no one place a group of them points at. focus() first,
       * or the tab is brought forward behind whatever the trader was doing.
       */
      notification.onclick = () => {
        window.focus()
        notification.close()
        navigate(fresh.length === 1 ? pathForArb(first.id) : '/notifications')
      }
    }
  }

  useEffect(() => () => clearTimeout(holdTimer.current), [])

  useEffect(() => { load() }, [load])
  useInterval(() => { load() }, 5000)

  const fixed = async (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id))
    await apiFetch(marketHost, '/api/notifications/fixed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    }).catch(() => {})
  }

  const mute = async (item, scope) => {
    const value = scope === 'event' ? `${item.home}|${item.away}|${item.koTime}`
      : scope === 'league' ? `${item.sport}|${item.league}`
        : item.sport
    const label = scope === 'event' ? `${item.home} vs ${item.away}`
      : scope === 'league' ? `${item.sport} · ${item.league}`
        : item.sport

    // it leaves the screen at once; the server is what keeps it gone
    setItems((prev) => prev.filter((other) => {
      if (scope === 'event') {
        return !(other.home === item.home && other.away === item.away && other.koTime === item.koTime)
      }
      if (scope === 'league') {
        return !(other.sport === item.sport && other.league === item.league)
      }
      return other.sport !== item.sport
    }))

    await apiFetch(marketHost, '/api/mutes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scope, value, label, koTime: scope === 'event' ? item.koTime : null })
    }).catch(() => {})
  }

  const shown = items ? (expanded ? items : items.slice(0, FIRST_PAGE)) : []

  return (
    <Box sx={{ padding: '24px' }}>
      <Box sx={{ backgroundColor: 'white', padding: '20px 24px 24px', maxWidth: '860px' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '4px' }}>
          <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>Arbs notifications</Typography>
          {items && items.length > 0 && (
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#9A9EA3' }}>
              {items.length}{items.length === MAX_PAGE ? '+' : ''} standing
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#6E7378', marginBottom: '16px' }}>
          An arb lands here the moment it crosses your threshold of {settings.minArbProfit}%. Mark fixed takes
          it off the list and keeps it quiet until one of its prices moves again.
        </Typography>

        {error && (
          <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: theme.palette.secondary.main }}>{error}</Typography>
        )}

        {items === null && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '30px' }}><CircularProgress size={26} /></Box>
        )}

        {items && items.length === 0 && (
          <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: '#9A9EA3', padding: '20px 0' }}>
            Nothing standing right now. New arbs appear here as they are found.
          </Typography>
        )}

        {shown.map((item) => (
          <Box
            key={item.id}
            /**
             * The actions stay on the right whatever the middle column holds. Letting the row wrap put
             * them underneath as soon as a three way moneyline made the line long, so one row looked
             * nothing like the row above it. The middle column shrinks and wraps its own text instead.
             */
            sx={{
              borderTop: '1px solid #EDEDF1',
              padding: '14px 0',
              display: 'flex',
              gap: '18px',
              alignItems: 'flex-start'
            }}
          >
            <Box sx={{ minWidth: '74px', flexShrink: 0 }}>
              <Typography sx={{
                fontFamily: FONT, fontSize: '19px', fontWeight: 700, color: theme.palette.primary.light
              }}>
                {item.profit.toFixed(2)}%
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                onClick={() => navigate(pathForArb(item.id))}
                sx={{
                  fontFamily: FONT, fontSize: '15px', fontWeight: 600, color: theme.palette.primary.main,
                  textDecoration: 'underline', cursor: 'pointer',
                  '&:hover': { color: theme.palette.primary.light }
                }}
              >
                {item.home} vs {item.away}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: '#9A9EA3' }}>
                {item.sport} · {item.league} · {clock.stamp(item.koTime)}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: '13.5px', marginTop: '6px', lineHeight: 1.6 }}>
                <Box component="span" sx={{ color: '#6E7378' }}>{item.market}: </Box>
                {item.legs.map((leg, index) => (
                  <Box component="span" key={index} sx={{ marginRight: '14px', whiteSpace: 'nowrap' }}>
                    {leg.outcome} <Box component="span" sx={{ fontWeight: 700 }}>{leg.price}</Box>
                    <Box component="span" sx={{ color: '#9A9EA3' }}> {leg.bookmaker}</Box>
                  </Box>
                ))}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: '11.5px', color: '#9A9EA3' }}>
                {timeAgo(item.createdAt)}
              </Typography>
              <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <MuteButton onClick={() => fixed(item.id)}>Mark fixed</MuteButton>
                <MuteButton onClick={() => mute(item, 'event')}>Mute event</MuteButton>
                <MuteButton onClick={() => mute(item, 'league')}>Mute league</MuteButton>
                <MuteButton onClick={() => mute(item, 'sport')}>Mute sport</MuteButton>
              </Box>
            </Box>
          </Box>
        ))}

        {items && items.length > FIRST_PAGE && (
          <Box sx={{ borderTop: '1px solid #EDEDF1', paddingTop: '14px' }}>
            <Box
              component="button"
              onClick={() => setExpanded(!expanded)}
              sx={{
                border: 'none', background: 'none', padding: 0, cursor: 'pointer',
                fontFamily: FONT, fontSize: '13px', color: theme.palette.primary.main, textDecoration: 'underline'
              }}
            >
              {expanded ? 'Show fewer' : `Show all ${items.length}`}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default NotificationsPage
